'use client';

import { ReactNode, useId } from 'react';

import { twMerge } from 'tailwind-merge';

import { Heading } from '@components/Heading/Heading';
import { ChevronDownIcon } from '@components/Icons/ChevronDownIcon';

export interface IAccordionData {
  id: string;
  title: ReactNode;
  body: ReactNode;
}

interface IAccordionProps {
  blocksData: IAccordionData[];
  className?: string;
  buttonClassName?: string;
  contentClassName?: string;
  overrideTitleClassName?: string;
  chevronClassName?: string;
  openFirstItemInitially?: boolean;
  chevronAlignment?: 'left' | 'right';
  // When true, opening a block closes any other open block (single-open mode).
  // Clicking the currently open block still collapses it. Default: false.
  singleOpen?: boolean;
}

export const Accordion = ({
  blocksData,
  className = '',
  buttonClassName = '',
  contentClassName = '',
  overrideTitleClassName = 'heading-text-xs text-left',
  chevronClassName = '',
  openFirstItemInitially = false,
  chevronAlignment = 'right',
  singleOpen = false,
}: IAccordionProps) => {
  // A shared `name` makes native <details> mutually exclusive (single-open).
  // useId keeps the group unique so multiple accordions don't interfere.
  const groupName = useId();

  return (
    <>
      {blocksData.map((blockData, index) => {
        const { title, body, id } = blockData;
        return (
          <details
            key={id}
            name={singleOpen ? groupName : undefined}
            open={openFirstItemInitially && index === 0}
            className={twMerge(
              'group w-full overflow-hidden rounded-md',
              // Animate the native disclosure content open/closed.
              '[interpolate-size:allow-keywords]',
              'details-content:h-0 details-content:overflow-hidden details-content:transition-all details-content:transition-discrete details-content:duration-300 details-content:ease-out',
              '[&[open]::details-content]:h-auto',
              className,
            )}
          >
            <summary
              className={twMerge(
                'flex w-full cursor-pointer list-none items-center justify-center p-4 leading-normal [&::-webkit-details-marker]:hidden',
                buttonClassName,
              )}
            >
              {typeof title === 'string' ? (
                <Heading as="div" variant="2xs" className={twMerge('mb-0', overrideTitleClassName)}>
                  {title}
                </Heading>
              ) : (
                title
              )}
              <div
                className={twMerge(
                  'text-abyss transition-transform duration-300 group-open:rotate-180',
                  chevronAlignment === 'left' ? 'order-first mr-4' : 'ml-auto',
                  chevronClassName,
                )}
              >
                <ChevronDownIcon />
              </div>
            </summary>
            <div className={twMerge('p-5 leading-normal', contentClassName)}>{body}</div>
          </details>
        );
      })}
    </>
  );
};
